import {
  CircleAlert,
  MailX,
  Server,
  UserCheck,
} from "lucide-react";
import { Trans, useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { Button } from "@/components/ui/Button";
import { ShcLogo } from "@/components/brand/ShcLogo";

type ErrorReason =
	| "expired"
	| "invalid"
	| "already-registered"
	| "server-error";

/** Reusable error card for all invitation-validation failures.*/
export function ErrorCard({
	reason,
	title,
	t,
	navigate,
	onRetry,
}: {
	reason: ErrorReason;
	title: string;
	t: ReturnType<typeof useTranslation<["auth"]>>["t"];
	navigate: ReturnType<typeof useNavigate>;
	onRetry?: () => void;
}) {
	const cardConfig: Record<
		ErrorReason,
		{ Icon: typeof MailX; i18nKey: string; strong: boolean }
	> = {
		expired: {
			Icon: MailX,
			i18nKey: "auth:signup.errors.invitation.expiredMessage",
			strong: true,
		},
		invalid: {
			Icon: CircleAlert,
			i18nKey: "auth:signup.errors.invitation.invalidMessage",
			strong: false,
		},
		"already-registered": {
			Icon: UserCheck,
			i18nKey: "auth:signup.errors.invitation.registeredMessage",
			strong: false,
		},
		"server-error": {
			Icon: Server,
			i18nKey: "auth:signup.errors.invitation.serverErrorMessage",
			strong: false,
		},
	};

	const { Icon, i18nKey, strong } = cardConfig[reason];
	const components: Record<string, React.ReactElement> = { br: <br /> };
	if (strong) components.strong = <strong style={{ color: "#991B1B" }} />;

	return (
		<>
			<div className="auth-card__logo">
				<div className="auth-card__logo-icon">
					<ShcLogo />
				</div>
				<h1 className="auth-card__title">{t("auth:signup.title")}</h1>
			</div>

			<div
				role="alert"
				aria-live="polite"
				style={{
					marginTop: "var(--space-4)",
					padding: "32px 28px",
					background: "rgba(255,255,255,0.95)",
					border: "1px solid rgba(252,165,165,0.6)",
					borderRadius: 16,
					boxShadow: "0 12px 28px rgba(15,23,42,0.15)",
					textAlign: "center",
					color: "#1f2937",
				}}
			>
				<div
					style={{
						display: "inline-flex",
						alignItems: "center",
						justifyContent: "center",
						width: 56,
						height: 56,
						borderRadius: "50%",
						background: "#FEE2E2",
						color: "#B91C1C",
						marginBottom: "var(--space-4)",
					}}
				>
					<Icon size={28} />
				</div>
				<h2
					className="auth-card__title"
					style={{
						marginBottom: "var(--space-2)",
						fontSize: 18,
						color: "#111827",
					}}
				>
					{title}
				</h2>
				<p
					className="auth-card__subtitle"
					style={{
						fontSize: 14,
						lineHeight: 1.7,
						color: "#4b5563",
						marginBottom: 22,
					}}
				>
					<Trans i18nKey={i18nKey} components={components} />
				</p>
				<Button
					variant="primary"
					className="btn--full"
					onClick={() => navigate("/login")}
				>
					{t("auth:signup.actions.expiredGoToLogin")}
				</Button>
				{onRetry && (
					<div style={{ marginTop: "var(--space-3)" }}>
						<Button variant="secondary" className="btn--full" onClick={onRetry}>
							{t("auth:signup.actions.retry")}
						</Button>
					</div>
				)}
			</div>
		</>
	);
}
